import { useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { PrimaryButton } from "components/Button/style";
import { FaColumns, FaEye, FaPlus, FaTrash, FaUpload } from "react-icons/fa";
import { utils, writeFileXLSX } from "xlsx";
import usePermissoes from "hooks/usePermissoes";
import { converterUtcParaBr } from "../../utils/data";
import { toastErro } from "components/Toast";
import { HttpResponse } from "_services/api/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IData } from "_services/api/interface/HttpResponse";
import { IListarLotePagamento } from "../../interfaces/IListarLotePagamento";
import {
  CHAVE_CONFIG_COLUNAS,
  colunaLabels,
  defaultConfigColunas,
} from "../../constants/defaultConfigColunas";
import { PERMISSAO_TAXA } from "../../constants/permissoes";
import { StatusLotePagamento } from "../StatusLotePagamento";
import { StatusLoteTaxaLabel } from "../StatusLotePagamento/enums/StatusLoteTaxa.enum";
import { ModalConfigColunas } from "components/ModalConfigColunas";
import { useConfigColunas } from "components/ModalConfigColunas/useConfigColunas";
import { ChipMais } from "components/ChipMais";
import {
  Container,
  DivAcoesLinha,
  Header,
  HeaderContainerBotoes,
  TextoHeader,
} from "./style";

interface IListarProps {
  dados: IListarLotePagamento[];
  versaoBusca: number;
  totalRows: number;
  loading: boolean;
  linhasPorPagina: number;
  handlePageChange: (e: number) => void;
  handleLimitChange: (e: number) => void;
  handleVerDetalhes: (idLote: number) => void;
  handleExcluir: (idLote: number) => void;
  onSort?: (coluna: TableColumn<any>, direcao: string) => void;
  onClickCriarLote: () => void;
  relatorioLotes: () => Promise<HttpResponse<any, any> | HttpError<IData, any>>;
}

export function ListarLotesPagamento({
  dados,
  versaoBusca,
  totalRows,
  loading,
  linhasPorPagina,
  handlePageChange,
  handleLimitChange,
  handleVerDetalhes,
  handleExcluir,
  onSort,
  onClickCriarLote,
  relatorioLotes,
}: IListarProps) {
  const { listaPermissoes } = usePermissoes();

  const podeExcluir =
    !listaPermissoes?.length ||
    listaPermissoes.some(
      (item) => item.nome_funcionalidade === PERMISSAO_TAXA.EXCLUIR_LOTE,
    );

  const [showModalColunas, setShowModalColunas] = useState(false);
  const { configColunas, isVisible, salvarConfigColunas } = useConfigColunas(
    CHAVE_CONFIG_COLUNAS,
    defaultConfigColunas,
  );

  const renderEmpresa = (row: IListarLotePagamento) => (
    <ChipMais
      itens={[row.empresaPrincipal, ...(row.outrasEmpresas ?? [])].filter(
        Boolean,
      ) as string[]}
    />
  );

  const columns: IColum<IListarLotePagamento>[] = [
    {
      name: "Lote",
      selector: ({ id }: IListarLotePagamento) => id,
      width: "110px",
      sortable: true,
      sortField: "id",
      omit: !isVisible("id"),
    },
    {
      name: "Usuário",
      selector: ({ usuario }: IListarLotePagamento) => usuario ?? "-",
      width: "160px",
      sortable: true,
      sortField: "usuario",
      omit: !isVisible("usuario"),
    },
    {
      name: "Cadastro",
      selector: ({ dataCadastro }: IListarLotePagamento) =>
        dataCadastro ? converterUtcParaBr(dataCadastro) : "-",
      width: "180px",
      sortable: true,
      sortField: "dataCadastro",
      omit: !isVisible("dataCadastro"),
    },
    {
      name: "Empresa",
      selector: renderEmpresa,
      sortable: true,
      sortField: "empresa",
      omit: !isVisible("empresa"),
    },
    {
      name: "Status",
      selector: ({ status }: IListarLotePagamento) => status,
      sortable: true,
      sortField: "status",
      cell: ({ status }: IListarLotePagamento) => (
        <StatusLotePagamento status={status} />
      ),
      width: "150px",
      omit: !isVisible("status"),
    },
    {
      name: "Qtd vinculado",
      selector: ({ qtdVinculado }: IListarLotePagamento) => qtdVinculado ?? 0,
      width: "150px",
      sortable: true,
      sortField: "qtdVinculado",
      omit: !isVisible("qtdVinculado"),
    },
    {
      name: "Qtd finalizado",
      selector: ({ qtdFinalizado }: IListarLotePagamento) => qtdFinalizado ?? 0,
      width: "150px",
      sortable: true,
      sortField: "qtdFinalizado",
      omit: !isVisible("qtdFinalizado"),
    },
    {
      name: "Ações",
      selector: () => "",
      cell: (row: IListarLotePagamento) => (
        <DivAcoesLinha>
          <FaEye
            size={15}
            title="Visualizar"
            onClick={() => handleVerDetalhes(row.id)}
            style={{ cursor: "pointer", color: "#555" }}
          />
          {podeExcluir &&
            (row.qtdVinculado === 0 ? (
              <FaTrash
                size={14}
                title="Excluir lote"
                onClick={() => handleExcluir(row.id)}
                style={{ cursor: "pointer", color: "#555" }}
              />
            ) : (
              <FaTrash
                size={14}
                title="Só é possível excluir lotes sem processos vinculados"
                style={{ cursor: "not-allowed", color: "#C9C9C9" }}
              />
            ))}
        </DivAcoesLinha>
      ),
      width: "100px",
    },
  ];

  const onClickExportar = async () => {
    const { hasErro, data } = await relatorioLotes();
    if (hasErro) {
      toastErro(data?.message ?? "Não foi possível gerar o relatório");
      return;
    }

    const itens: IListarLotePagamento[] = data.itens ?? [];

    const col = (
      label: string,
      config: string | null,
      wch: number,
      valor: (item: IListarLotePagamento) => string | number,
    ) => ({ label, config, wch, valor });

    const colunasRelatorio = [
      col("ID lote", "id", 10, (item) => item.id ?? "-"),
      col("Usuário", "usuario", 20, (item) => item.usuario ?? "-"),
      col("Cadastro", "dataCadastro", 18, (item) =>
        item.dataCadastro ? converterUtcParaBr(item.dataCadastro) : "-",
      ),
      col("Empresa", "empresa", 30, (item) =>
        [item.empresaPrincipal, ...(item.outrasEmpresas ?? [])]
          .filter(Boolean)
          .join(" | "),
      ),
      col("Status", "status", 14, (item) => StatusLoteTaxaLabel[item.status] ?? "-"),
      col("Qtd vinculado", "qtdVinculado", 14, (item) => item.qtdVinculado ?? 0),
      col("Qtd finalizado", "qtdFinalizado", 14, (item) =>
        item.qtdFinalizado ?? 0,
      ),
    ].filter(({ config }) => config === null || isVisible(config));

    const dadosPlanilha = itens.map((item) =>
      Object.fromEntries(
        colunasRelatorio.map(({ label, valor }) => [label, valor(item)]),
      ),
    );

    const planilha = utils.json_to_sheet(dadosPlanilha);
    planilha["!cols"] = colunasRelatorio.map(({ wch }) => ({ wch }));
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Lotes");

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();
    writeFileXLSX(workBook, `Pagamento_Taxa_SP_${dia}-${mes}-${ano}.xlsx`);
  };

  return (
    <Container>
      <ModalConfigColunas
        show={showModalColunas}
        handleClose={() => setShowModalColunas(false)}
        configColunas={configColunas}
        onSave={salvarConfigColunas}
        labels={colunaLabels}
      />
      <Header>
        <TextoHeader>Lotes para pagamento</TextoHeader>
        <HeaderContainerBotoes>
          <PrimaryButton
            type="button"
            onClick={onClickExportar}
            outline
            variante="primary"
            title="Exportar"
            style={{ fontSize: 12 }}
          >
            <FaUpload />
          </PrimaryButton>
          <PrimaryButton
            type="button"
            onClick={() => setShowModalColunas(true)}
            outline
            variante="primary"
            title="Configurar colunas"
            style={{ fontSize: 12 }}
          >
            <FaColumns />
          </PrimaryButton>
          <PrimaryButton
            type="button"
            onClick={onClickCriarLote}
            buttonWidth="lg"
            buttonWidthTipo="px"
            style={{ fontSize: 12 }}
          >
            <FaPlus /> Criar novo lote
          </PrimaryButton>
        </HeaderContainerBotoes>
      </Header>
      <hr />
      <Table
        key={`${linhasPorPagina}-${versaoBusca}`}
        keyField="id"
        totalRows={totalRows}
        pagination={true}
        columns={columns}
        data={dados}
        paginationRowsPerPageOptions={[30, 50, 100, 200]}
        loading={loading}
        noSubHeader
        possuiTooltip={false}
        selectableRows
        headerFixo
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleLimitChange}
        rowsPerPage={linhasPorPagina}
        handleSortFunction={onSort}
      />
    </Container>
  );
}
